import { ChangeDetectorRef, Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { CartResultLineItemSelection, GroupedCartResultLineItemSelection } from 'entities/cart-result';
import { VendorPartPriceResult } from 'entities/hot-flag/get-vendor-part-prices-result';
import { HotFlagResult } from 'entities/hot-flag/hot-flag-result';
import { groupCumminsVinItems } from 'helpers/group-cummins-vin-items';
import { BehaviorSubject, combineLatest, Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, map, tap, withLatestFrom } from 'rxjs/operators';
import { FeatureFlagService } from 'services/feature-flag.service';
import { AppState } from 'store/app-state';
import * as BackCounterRequestActions from 'store/back-counter/back-counter.actions';
import { OpeningFrom, RequestOrderItem } from 'store/back-counter/back-counter.entities';
import * as CartActions from 'store/cart/cart.actions';
import * as CartSelectors from 'store/cart/cart.selectors';
import * as FeatureFlagSelectors from 'store/feature-flags/feature-flags.selectors';
import * as MyCartActions from 'store/my-cart/my-cart.actions';
import * as MyCartSelectors from 'store/my-cart/my-cart.selectors';

type CartItemFormGroup = {
    cartItemId: FormControl<string | null>;
    hotFlagCode: FormControl<string>;
    bctId: FormControl<string | null>;
    esn: FormControl<string | null>;
    crc: FormControl<string | null>;
    vin?: FormControl<string | null>;
};

type HotFlagFormGroup = {
    vin: FormControl<string | null>;
    cartItems: FormArray<FormGroup<CartItemFormGroup>>;
};

@Component({
    selector: 'hot-flag',
    templateUrl: './hot-flag.component.html',
    standalone: false
})
export class HotFlagComponent implements OnInit, OnDestroy {
    HOT_FLAG_CODE_WARRANTY = "21";
    CUMMINS_VENDORS = [ "220980", "242190" ];

    @Input() public editHotFlagCode?: string;
    @Input() openingFrom: OpeningFrom;
    @Input() requestOrderItems: RequestOrderItem[];
    @Input() orderNumber: string;
    @Input() requestBranchCode: string;
    @Input() esn?: string;

    form: FormGroup<HotFlagFormGroup> = this.fb.group({
        vin: new FormControl<string>('', []),
        cartItems: new FormArray([])
    });

    vinValidators = [
        Validators.required,
        Validators.minLength(1),
        Validators.pattern(/^[a-zA-Z0-9]*$/)
    ];

    esnValidators = [
        Validators.minLength(8),
        Validators.required
    ];

    private filterSubject = new BehaviorSubject<string>('');
    filterChangeAction$: Observable<string> = this.filterSubject.asObservable();

    hotFlags$: Observable<HotFlagResult[]> = this.store.select(MyCartSelectors.hotFlags);
    vinHotFlagCodes$: Observable<string[]> = this.hotFlags$.pipe(
        map((hotFlags) => hotFlags.filter(hf => hf.isVINRequired === true).map(hf => hf.code)),
        tap((hotFlags) => this.vinHotFlagCodes = hotFlags)
    );
    cartVin: Subscription = this.store.select(CartSelectors.selectVin).pipe(
        tap((vin) => {
            if(this.openingFrom === 'CART'){
                this.form.patchValue({vin});
                this.form.controls.vin.updateValueAndValidity({ emitEvent: false });
            }
        })
    ).subscribe();
    selectedCartItems$: Observable<CartResultLineItemSelection[]> = this.store.select(MyCartSelectors.selectedCartItems).pipe(
        tap((selectedCartItems) => {
            if (selectedCartItems.length === 0 && this.openingFrom === 'CART') {
                this.activeModal.close();
            }
        })
    );

    cartItemsWithVendorPrices$ = this.store.select(MyCartSelectors.vendorPartsPriceResult).pipe(tap(t => {
        this.requestOrderItems = this.requestOrderItems?.map(m => {
            const item = t.find(f => f.partNumber === m.materialNumber);
            if(item) {
                return {
                    ...m,
                    vendorCode: item?.vendorNumber ??''
                }
            } else {
                return m;
            }
        })
    }));

  removedItemBctId = "";

  filteredCartItems$: Observable<GroupedCartResultLineItemSelection[]> = combineLatest(
    [
      this.selectedCartItems$,
      this.filterChangeAction$,
      this.cartItemsWithVendorPrices$,
    ]
  ).pipe(
    withLatestFrom(this.vinHotFlagCodes$),
    tap(
      ([[selectedCartItems, _, __], ___]: [
        [CartResultLineItemSelection[], string, VendorPartPriceResult[]],
        string[]
      ]) => {
        // Only rebuild form if the list of selected items has changed (not just item properties)
        // For BCT context, use requestOrderItems IDs; for CART context, use selectedCartItems IDs
        const currentItemIds = this.openingFrom === "CART"
          ? (selectedCartItems?.map(item => item.cartItemId) || [])
          : (this.requestOrderItems?.filter(roi => roi._id !== this.removedItemBctId).map(item => item._id) || []);

        const itemIdsChanged = currentItemIds.length !== this.lastFormItemIds.length ||
          !currentItemIds.every((id, idx) => id === this.lastFormItemIds[idx]);

        if (itemIdsChanged) {
          this.lastFormItemIds = currentItemIds;
          if (this.openingFrom === "CART") {
            const grouped = groupCumminsVinItems(selectedCartItems);
            this.displayedGroupedItems = grouped;
            this.initForm(grouped);
          } else {
            this.requestOrderItems = this.requestOrderItems?.filter(
              (roi) => roi._id !== this.removedItemBctId
            );
            this.initForm(this.requestOrderItems);
          }
        }
      }
    ),
    map(
      ([[selectedCartItems, filterString, vendorPartPriceResult], _]: [
        [CartResultLineItemSelection[], string, VendorPartPriceResult[]],
        string[]
      ]) => {
        if (this.openingFrom === "CART") {
          const groupedItems = groupCumminsVinItems(selectedCartItems);
          const filteredItems = groupedItems.filter((selectedCartItem) => {
            if (filterString === "") {
              return true;
            }
            return (
              selectedCartItem.partNumber
                ?.toLowerCase()
                .includes(filterString.toLowerCase()) ||
              selectedCartItem.corePartNumber
                ?.toLowerCase()
                .includes(filterString.toLowerCase())
            );
          });
          this.selectedCartItemsLength = filteredItems?.length;
          return filteredItems;
        } else {
          const vpResult = vendorPartPriceResult.reduce((map, obj) => {
            map[obj.partNumber] = obj;
            return map;
          }, {} as Record<string, VendorPartPriceResult>);

          const cartResultLineItemSelections = this.requestOrderItems.map(
            (m) => {
              return {
                partNumber: m.materialNumber,
                bctId: m._id,
                vendorCode: vpResult[m.materialNumber]?.vendorNumber ?? "",
                knownPartPrice: vpResult[m.materialNumber]?.purchasePrice ?? 0,
                groupedCartItemIds: [m._id],
                groupedQuantity: 1,
                isGrouped: false,
                underlyingItems: []
              } as GroupedCartResultLineItemSelection;
            }
          );

          const filteredItems = cartResultLineItemSelections.filter((item) => {
            if (filterString === "") {
              return true;
            }
            return item.partNumber
              ?.toLowerCase()
              .includes(filterString.toLowerCase());
          });

          this.selectedCartItemsLength = filteredItems.length;
          return filteredItems;
        }
      }
    )
  );

    selectedCartItemsLength = 0;
    filterKey: string = '';
    partNumber: string = '';

    fixedVendor$: Observable<boolean> = combineLatest([
        this.store.select(MyCartSelectors.selectedCartItems),
        this.store.select(MyCartSelectors.vendorPartsPriceResult)
    ]).pipe(
        map(([selectedItems, vendorPriceResults]) => {
            this.partNumber = '';
            // For BCT context, check vendorPartsPriceResult against current requestOrderItems
            if (this.openingFrom === 'BCT' && this.requestOrderItems?.length > 0) {
                const hasMissingVendor = this.requestOrderItems.some(item => {
                    const vendorResult = vendorPriceResults.find(v => v.partNumber === item.materialNumber);
                    const hasNoVendor = !vendorResult?.vendorNumber || vendorResult.vendorNumber.length === 0;
                    if (hasNoVendor && !this.partNumber) {
                        this.partNumber = item.materialNumber;
                    }
                    return hasNoVendor;
                });
                return !hasMissingVendor;
            }
            // For CART context, check selectedCartItems
            const hasMissingVendor = selectedItems.some(selected => {
                const hasNoVendor = !selected.vendorCode || selected.vendorCode.length === 0;
                if (hasNoVendor && !this.partNumber) {
                    this.partNumber = selected.partNumber;
                }
                return hasNoVendor;
            });
            return !hasMissingVendor;
        })
    );
    isHotFlagVinEnabled$ = this.store.select(FeatureFlagSelectors.isFeatureActive('Hotflag.VinNumber'));
    lastExpandedIndex: number = null;
    vinHotFlagCodes: string[] = [];
    subscription: Subscription;
    isVINUpdating: boolean = false;

    vinUpdateSubject = new Subject<{event: InputEvent, index: number, vendorCode: string}>();
    esnUpdateSubject = new Subject<{event: InputEvent, id: string, index: number}>();
    crcUpdateSubject = new Subject<{event: InputEvent, id: string, index: number}>();

    vinUpdate = this.vinUpdateSubject.pipe(
        tap(() => this.isVINUpdating = true),
        debounceTime(250),
        tap(({ event, index, vendorCode }) => {
            this.focusElementId = (event.target as HTMLElement).id;

            // Always update the VIN error state regardless of form validity
            this.setVINError();

            const cartItemControl = this.form.controls.cartItems.controls[index]?.controls.vin;
            const rootVinControl = this.form.controls.vin;

            // Check validity based on which VIN control is being used
            const vinControl = this.openingFrom === 'CART' && cartItemControl ? cartItemControl : rootVinControl;

            if (!vinControl?.valid) {
                // Trigger change detection to ensure error state is updated in UI
                this.changeDetectionRef.detectChanges();
                return;
            }

            if(this.openingFrom === 'CART') {
                const vinValue = cartItemControl?.value || rootVinControl?.value;
                this.store.dispatch(CartActions.updateVIN({ vin: vinValue }));
                // Update per-item VIN if feature is enabled — dispatch for all underlying grouped items
                if (this.featureFlagService.isFeatureActive('Hotflag.VinNumber') && cartItemControl) {
                    const primaryCartItemId = this.form.controls.cartItems.controls[index]?.controls.cartItemId.value;
                    const groupedItem = this.displayedGroupedItems.find(g => g.cartItemId === primaryCartItemId);
                    const cartItemIds = groupedItem?.groupedCartItemIds ?? [primaryCartItemId];
                    for (const cartItemId of cartItemIds) {
                        this.store.dispatch(MyCartActions.updateCartItemVIN({ cartItemId, vin: cartItemControl.value }));
                    }
                }
            }

            this.form.controls.cartItems.controls.forEach((g: FormGroup<CartItemFormGroup>) => {
                if (this.isVINRequiredForHotFlag(g.controls.hotFlagCode.value)) {
                    const primaryId = g.controls.cartItemId.value;
                    const groupedItem = this.displayedGroupedItems.find(gi => gi.cartItemId === primaryId);
                    const cartItemIds = groupedItem?.groupedCartItemIds ?? [primaryId];
                    for (const cartItemId of cartItemIds) {
                        this.store.dispatch(MyCartActions.updateCartItemHotFlag({
                            cartItemId,
                            hotFlagCode: g.controls.hotFlagCode.value,
                        }));
                    }
                }
            });
        }),
        tap(() => this.isVINUpdating = false)
    ).subscribe();

    esnUpdate = this.esnUpdateSubject.pipe(
        debounceTime(250),
        tap(({ id, index }) => {
            this.focusElementId = `hotFlag_esn_cartItemHotFlag_${index}`;
            if (!this.form.controls.cartItems.controls[index].controls.esn.valid) {
                this.setESNError();
                return;
            }
            const groupedItem = this.displayedGroupedItems.find(g => g.cartItemId === id);
            const cartItemIds = groupedItem?.groupedCartItemIds ?? [id];
            for (const cartItemId of cartItemIds) {
                this.store.dispatch(MyCartActions.updateCartItemESN({ cartItemId, esn: this.form.value.cartItems[index].esn }));
            }
        })
    ).subscribe();

    crcUpdate = this.crcUpdateSubject.pipe(
        debounceTime(250),
        tap(({ id, index }) => {
            this.focusElementId = `hotFlag_crc_cartItemHotFlag_${index}`;
            if (!this.form.controls.cartItems.controls[index].controls.crc.valid) {
                return;
            }
            const groupedItem = this.displayedGroupedItems.find(g => g.cartItemId === id);
            const cartItemIds = groupedItem?.groupedCartItemIds ?? [id];
            for (const cartItemId of cartItemIds) {
                this.store.dispatch(MyCartActions.updateCartItemCRC({ cartItemId, crc: this.form.value.cartItems[index].crc }));
            }
        })
    ).subscribe();

    focusElementId: string;
    vinControlSub: Subscription;
    showVINControl: boolean = false;
    showESNWithCRC: boolean = false;
    hasVINError: boolean = false;
    hasESNError: boolean = false;
    hasHotFlagError: boolean = false;
    lastFormItemIds: string[] = []; // Track cart item IDs to prevent unnecessary form rebuilds
    displayedGroupedItems: GroupedCartResultLineItemSelection[] = []; // Track grouped items for dispatch lookups

    changeDetectionRef = inject(ChangeDetectorRef);
    bctItems: { [key: string]: { vendor: string, knownPartPrice: number, hotFlagValue: string } } = {};

    constructor(
        private featureFlagService: FeatureFlagService,
        private store: Store<AppState>,
        private activeModal: NgbActiveModal,
        private fb: FormBuilder,
    ) { }

    ngOnInit() {
        this.store.dispatch(MyCartActions.getHotFlags());
        if(this.openingFrom === 'CART') {
            this.store.dispatch(MyCartActions.getVendorPrices({ vendorCode: null, openingFrom: this.openingFrom }));
        } else {
            this.store.dispatch(MyCartActions.getVendorPrices({ vendorCode: null, openingFrom: this.openingFrom, items: this.requestOrderItems, requestBranchCode: this.requestBranchCode }));
        }
        this.changeDetectionRef.detectChanges();
    }

    ngOnDestroy(): void {
        this.cartVin.unsubscribe();
        this.vinUpdate.unsubscribe();
        this.crcUpdate.unsubscribe();
        this.esnUpdate.unsubscribe();
    }

    initForm(selectedCartItems: GroupedCartResultLineItemSelection[] | RequestOrderItem[]) {
        if (this.editHotFlagCode) {
            this.lastExpandedIndex = 0;
            this.focusElementId = 'hotFlag_vin_cartItemHotFlag_0';
        }
        const crcValue = this.form.controls.cartItems.controls[this.lastExpandedIndex]?.controls?.crc?.value ?? '';
        const esnValue =
          this.form.controls.cartItems.controls[this.lastExpandedIndex]?.controls?.esn?.value?.length > 0
            ? this.form.controls.cartItems.controls[this.lastExpandedIndex]?.controls?.esn?.value
            : (this.esn ?? '');

        const formValue = this.form.value;
        this.form.controls.cartItems.clear();
        const shouldAddItemVin = this.openingFrom === 'CART' && this.featureFlagService.isFeatureActive('Hotflag.VinNumber');

        if(this.openingFrom === 'CART') {
            (selectedCartItems as GroupedCartResultLineItemSelection[]).forEach((ci: GroupedCartResultLineItemSelection, index: number) => {
                const hotFlagCode = this.editHotFlagCode && index === 0
                    ? this.editHotFlagCode
                    : ci.hotFlagCode;
                // ESN required for any warranty hot flag (21)
                const isESNRequired = this.isWarranty(hotFlagCode);
                const formGroupConfig: any = {
                    cartItemId: new FormControl<string>(ci.cartItemId),
                    hotFlagCode: new FormControl<string>(hotFlagCode, [Validators.required]),
                    bctId: new FormControl(null),
                    crc: new FormControl<string>(ci?.crc ?? ''),
                    esn: new FormControl<string>(ci?.esn ?? '', isESNRequired ? this.esnValidators : [])
                };

                // Add per-item VIN only when opening from CART and feature flag is active
                if (shouldAddItemVin) {
                    formGroupConfig.vin = new FormControl<string>(ci?.vin ?? '');
                }

                this.form.controls.cartItems.push(this.fb.group(formGroupConfig) as unknown as FormGroup<CartItemFormGroup>);

                // Populate per-item VIN from root VIN only if the cart item doesn't already have a VIN value
                if (shouldAddItemVin) {
                    const newItemControl = this.form.controls.cartItems.at(index);
                    const itemVin = newItemControl?.controls?.vin?.value;
                    // Only populate from root VIN if item VIN is empty
                    if (newItemControl?.controls?.vin && !itemVin) {
                        const currentRootVin = this.form.controls.vin.value;
                        if (currentRootVin) {
                            newItemControl.controls.vin.patchValue(currentRootVin);
                            newItemControl.controls.vin.updateValueAndValidity({ emitEvent: false });
                        }
                    }
                }

                if(isESNRequired) {
                    this.setESNError();
                }
            });
        } else {
            (selectedCartItems as RequestOrderItem[]).forEach((roi: RequestOrderItem, index: number) => {
                // Find the previous hot flag value for this specific part by matching bctId instead of relying on array index
                const previousFormItem = formValue.cartItems?.find(item => item.bctId === roi._id);
                const hotFlagCode = (this.editHotFlagCode && index === 0
                    ? this.editHotFlagCode
                    : previousFormItem?.hotFlagCode) ?? roi.hotflag ?? '';
                // ESN required for any :CE warranty hot flag (21)
                const isESNRequired = this.setCumminsWithWarrantyState(index, roi.vendorCode);
                const formGroupConfig: any = {
                    hotFlagCode: new FormControl<string>(hotFlagCode, [Validators.required]),
                    cartItemId: new FormControl<string>(roi._id),
                    bctId: new FormControl<string>(roi._id),
                    crc: new FormControl<string>(crcValue),
                    esn: new FormControl<string>(esnValue, isESNRequired ? this.esnValidators : [])
                };

                this.form.controls.cartItems.push(this.fb.group(formGroupConfig) as unknown as FormGroup<CartItemFormGroup>);

                if(isESNRequired) {
                    this.setESNError();
                }
            });
        }

         if(this.featureFlagService.isFeatureActive('Hotflag.VinNumber')){

          this.setVinValidators();
          this.setVinTrim();
          this.setVINError(); // Check VIN validation state after setting validators
        }
        this.setHotFlagCodeRequiredError();
        this.setESNError();
        this.form.updateValueAndValidity();

        // Initialize VIN control visibility and ESN/CRC control visibility for each cart item after form is ready
        if(this.featureFlagService.isFeatureActive('Hotflag.VinNumber')){
            this.form.controls.cartItems.controls.forEach((_, index) => {
            const vendorCode = this.openingFrom === 'CART'
                ? (selectedCartItems as GroupedCartResultLineItemSelection[])[index]?.vendorCode
                : (selectedCartItems as RequestOrderItem[])[index]?.vendor;
            if (vendorCode) {
                this.setEsnValidators(index, vendorCode);
                this.setShowVINControl(index, vendorCode);
                }

            });
        }
        if (this.focusElementId) {
            setTimeout(() => document.querySelector<HTMLInputElement>(`#${this.focusElementId}`)?.focus());
        }
    }

    public closeModal(): void {
        this.activeModal.close();
    }

    filter() {
        this.filterSubject.next(this.filterKey);
    };

    removeCartItem(cartItem: GroupedCartResultLineItemSelection) {
        if(this.openingFrom === "BCT") {
            this.removedItemBctId = cartItem?.bctId ?? "";
            // Clean up the bctItems to prevent removed parts from being included in hot flag updates
            if (cartItem.bctId && this.bctItems[cartItem.bctId]) {
                delete this.bctItems[cartItem.bctId];
            }
            this.requestOrderItems = this.requestOrderItems?.filter(
                requestOrderItem => requestOrderItem._id !== cartItem.bctId
            );
            this.partNumber = '';
            // For Back Counter requests, we need to uncheck the part in the underlying form
            // Dispatch an action to notify the table component to uncheck this item
            if (cartItem.bctId) {
                this.store.dispatch(BackCounterRequestActions.removePartFromProcurement({
                    partId: cartItem.bctId,
                    orderNumber: this.orderNumber
                }));
            }
            this.changeDetectionRef.detectChanges();
        }
        // Deselect ALL underlying cart item IDs for grouped items
        const cartItemIds = cartItem.groupedCartItemIds ?? [cartItem.cartItemId];
        for (const cartItemId of cartItemIds) {
            this.store.dispatch(MyCartActions.toggleSelection({ cartItemId }));
        }
    }

    sendHotFlags() {
        if(this.openingFrom === 'CART') {
            this.store.dispatch(MyCartActions.sendHotFlag());
        } else {
            const vin = this.form.value.vin;
            const esn = this.form.value.cartItems.find(f => f.esn?.length > 0)?.esn ?? null;
            const crc = this.form.value.cartItems.find(f => f.crc?.length > 0)?.crc ?? null;
            this.store.dispatch(BackCounterRequestActions.updateHotFlagValue({
                orderNumber: this.orderNumber,
                items: this.bctItems,
                esn,
                crc,
                vin
            }));
            this.closeModal();
        }
    }

    onHotFlagChange(index: number, hotFlagValue: string, vendorCode: string, knownPartPrice: number) {
       const controls = this.getCartItemControls(index);
       // Update the existing control value instead of creating a new one
       controls.hotFlagCode.setValue(hotFlagValue);

          if(this.featureFlagService.isFeatureActive('Hotflag.VinNumber')){

          this.setVinValidators();
          this.setVinTrim();
          this.setVINError();
        }

        this.setEsnValidators(index, vendorCode);
        this.setBCTData(controls, vendorCode, knownPartPrice);
        this.updateCartItemHotFlag(controls, vendorCode);
        this.setShowVINControl(index, vendorCode);
        this.setHotFlagCodeRequiredError();

        // Trigger change detection to ensure UI updates
        this.changeDetectionRef.detectChanges();
    }

    onAccordionShown(index: number) {
        this.lastExpandedIndex = index;
    }

    setBCTData(controls: CartItemFormGroup, vendorCode: string, knownPartPrice: number = 0) {
        if (!controls.hotFlagCode.valid) {
            return;
        }
        const bctId = controls.bctId.value;
        const hotFlagCode = controls.hotFlagCode.value;
        this.bctItems[bctId] = { vendor: vendorCode, knownPartPrice: knownPartPrice, hotFlagValue: hotFlagCode };
    }

    setEsnValidators(index: number, vendorCode: string) {
        const hotFlagCode = this.form.controls.cartItems.controls[index]?.controls?.hotFlagCode?.value || '';
        const isESNRequired = this.setCumminsWithWarrantyState(index, vendorCode);

        if(isESNRequired) {
            this.form.controls.cartItems.controls[index].controls.esn.setValidators(this.esnValidators);
            // Mark as touched to ensure validation styling appears
            this.form.controls.cartItems.controls[index].controls.esn.markAsTouched();
            this.setESNError();
        } else {
            this.form.controls.cartItems.controls[index].controls.esn.clearValidators();
        }
        this.form.controls.cartItems.controls[index].controls.esn.updateValueAndValidity();
    }

    private setVinValidators() {
        const isVINRequired = this.form.controls.cartItems.controls.some((g: FormGroup<CartItemFormGroup>) => this.isVINRequiredForHotFlag(g.controls.hotFlagCode.value));
        const shouldAddItemVin = this.openingFrom === 'CART' && this.featureFlagService.isFeatureActive('Hotflag.VinNumber');

        // Set validators for root-level VIN
        if (isVINRequired && !shouldAddItemVin) {
            this.form.controls.vin.setValidators(this.vinValidators);
            this.form.controls.vin.markAsTouched();
            this.form.controls.vin.markAsDirty();
        } else {
            this.form.controls.vin.clearValidators();
        }
        this.form.controls.vin.updateValueAndValidity();

        // Set validators for per-item VIN if applicable
        if (shouldAddItemVin) {
            this.form.controls.cartItems.controls.forEach((g: FormGroup<CartItemFormGroup>) => {
                if (g.controls.vin) {
                    if (this.isVINRequiredForHotFlag(g.controls.hotFlagCode.value)) {
                        g.controls.vin.setValidators(this.vinValidators);
                        g.controls.vin.markAsTouched();
                        g.controls.vin.markAsDirty();
                    } else {
                        g.controls.vin.clearValidators();
                    }
                    g.controls.vin.updateValueAndValidity();
                }
            });
        }

        this.setVINError();
    }

    setVinTrim() {
        // Trimming moved to blur event in template to avoid focus loss during typing
        // This method is kept for compatibility but trim is now handled on blur
    }

    trimVINValue(control: FormControl<string | null>) {
        if (!control?.value) {
            return;
        }
        const trimmedValue = control.value.replaceAll(' ', '');
        if (trimmedValue !== control.value) {
            control.setValue(trimmedValue, { emitEvent: false });
        }
    }

    updateCartItemHotFlag(controls: CartItemFormGroup, vendorCode?: string) {
        if (!controls.hotFlagCode.valid) {
            return;
        }

        const shouldAddItemVin = this.openingFrom === 'CART' && this.featureFlagService.isFeatureActive('Hotflag.VinNumber');
        const vinValid = shouldAddItemVin ? controls.vin?.valid : this.form.controls.vin.valid;

        if (this.openingFrom === 'CART' && (!this.isVINRequiredForHotFlag(controls.hotFlagCode.value) || vinValid)) {
            // Find the grouped item to dispatch for all underlying cart item IDs
            const groupedItem = this.displayedGroupedItems.find(g => g.cartItemId === controls.cartItemId.value);
            const cartItemIds = groupedItem?.groupedCartItemIds ?? [controls.cartItemId.value];
            for (const cartItemId of cartItemIds) {
                this.store.dispatch(MyCartActions.updateCartItemHotFlag({
                    cartItemId,
                    hotFlagCode: controls.hotFlagCode.value,
                }));
            }
        }
    }

    setShowVINControl(index: number, vendorCode: string) {
        const hotFlagCode = this.form.controls.cartItems.controls[index]?.controls?.hotFlagCode?.value || '';
        this.showVINControl = (this.isVINRequiredForHotFlag(hotFlagCode) || this.setCumminsWithWarrantyState(index, vendorCode)) && this.featureFlagService.isFeatureActive('Hotflag.VinNumber');
        this.setVINError();
    }

    isVINControlVisible(index: number, vendorCode: string) {
        const hotFlagCode = this.form.controls.cartItems.controls[index]?.controls?.hotFlagCode?.value || '';
        return (this.isVINRequiredForHotFlag(hotFlagCode) || this.setCumminsWithWarrantyState(index, vendorCode)) && this.featureFlagService.isFeatureActive('Hotflag.VinNumber');
    }

    setCumminsWithWarrantyState(index: number, vendorCode: string): boolean {
        const hotFlagCode = this.form.controls.cartItems.controls[index]?.controls?.hotFlagCode?.value || '';
        const value =  this.isCummins(vendorCode) && this.isWarranty(hotFlagCode);

        this.showESNWithCRC = value ;

        this.setESNError();
        return value;
    }

    updateCumminsWithWarrantyVisibility(index: number, vendorCode: string): boolean {
        const hotFlagCode = this.form.controls.cartItems.controls[index]?.controls?.hotFlagCode?.value || '';
        const value =  this.isCummins(vendorCode) && this.isWarranty(hotFlagCode);
        return value;
    }

    isCummins(vendorCode: string): boolean {
        return this.CUMMINS_VENDORS.some(s => vendorCode?.includes(s));
    }

    isWarranty(hotFlagCode: string) {
        return this.HOT_FLAG_CODE_WARRANTY === hotFlagCode;
    }

    isVINRequiredForHotFlag(hotFlagCode: string): boolean {
        return this.vinHotFlagCodes.includes(hotFlagCode);
    }

    getCartItemControls(index: number): CartItemFormGroup {
        return this.form.controls.cartItems.controls[index]?.controls;
    }

    getFormIndexByCartItem(cartItem: CartResultLineItemSelection): number {
        const itemId = cartItem.cartItemId ?? cartItem.bctId;
        return this.form.controls.cartItems.controls.findIndex(
            (control) => control.controls.cartItemId.value === itemId || control.controls.bctId.value === itemId
        );
    }

    getVINControl(index: number): FormControl<string | null> {
        // Return per-item VIN if it exists (when openingFrom === 'CART' and feature flag is active)
        // Otherwise return root-level VIN
        const itemVin = this.form.controls.cartItems.controls[index]?.controls?.vin;
        return itemVin || this.form.controls.vin;
    }

    hasVINPatternError(): boolean {
        const rootError = this.form.controls.vin.errors?.pattern;
        if (rootError) return true;

        return this.form.controls.cartItems.controls.some(g => g.controls.vin?.errors?.pattern);
    }

    hasVINRequiredError(): boolean {
        const rootError = this.form.controls.vin.errors?.required || this.form.controls.vin.errors?.minlength;
        if (rootError) return true;

        return this.form.controls.cartItems.controls.some(g =>
            g.controls.vin?.errors?.required || g.controls.vin?.errors?.minlength
        );
    }

    setHotFlagCodeRequiredError() {
        this.hasHotFlagError = this.form.controls.cartItems.controls.some(g => g.controls.hotFlagCode.errors?.required);
    }

    setVINError() {
        let hasError = false;

        // Check root-level VIN errors
        const rootVinErrors = this.form.controls.vin.errors;
        if (rootVinErrors) {
            hasError = true;
        }

        // Check per-item VIN errors if they exist
        this.form.controls.cartItems.controls.forEach(g => {
            const itemVinControl = g.controls.vin;
            if (itemVinControl?.errors) {
                hasError = true;
            }
        });

        this.hasVINError = hasError;

        // Trigger change detection to ensure the error state is reflected in the UI
        this.changeDetectionRef.detectChanges();
    }

    setESNError() {
        const minlengthError = this.form.controls.cartItems.controls.some(s => s.controls.esn.errors?.minlength);
        const requiredError = this.form.controls.cartItems.controls.some(s => s.controls.esn.errors?.required);
        this.hasESNError =  minlengthError || requiredError;
    }

    accordionItemShown(index: number, vendorCode: string) {
        this.setShowVINControl(index, vendorCode);
        this.setCumminsWithWarrantyState(index, vendorCode);
    }


}
